/**
 * 67.61 (2): the planet's ground as the litter sees it (rendering/groundLitter.ts `LitterGround`): the drawn ground along up
 * (the fine patch's `drawnGround` when it is drawn, else the person-scale surface), the forest's biome there
 * (`forestBiomeAt`), the ground's material (`groundMaterialAt`), and no litter on a standing structure's footprint (a
 * person of room round it; vehicles drive over it), and the region's density (`litterDensity`: the biome, the material
 * and the planet's climate from its weather mix). The planet is read through `planet()` each call, so a crater — a new
 * planet state — is seen at once; the structures' discs are rebuilt when the structures change. Presentation only: it
 * reads the state, writes nothing.
 */
import type { GameState, PlanetState, Vec3 } from '../types';
import { litterDensity, litterClimate, LITTER, type LitterGround } from '../rendering/groundLitter';
import { BIOME_WEATHER } from '../core/weather';
import { forestBiomeAt, planetForestSeed } from '../core/forest';
import { groundMaterialAt } from '../core/groundMaterial';
import { personSurfaceRadius } from '../core/fineTerrain';
import { personSetOf, PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { structureFootprint } from '../core/buildingScale';

/** A standing structure's footprint on the planet: its direction and the cosine of its angular radius. */
interface Disc { x: number; y: number; z: number; cos: number }

export function planetLitterGround(state: () => GameState, planetIndex: number, drawnGround?: (planet: PlanetState, dir: Vec3) => number): LitterGround {
  const s0 = state(), ps = personSetOf(s0), person = PERSON_BODY_HEIGHT * ps.k;
  let seedFor: PlanetState | null = null, seed = '';
  let discsFor: GameState['structures'] | null = null, discs: Disc[] = [];
  const planet = () => state().planets[planetIndex];
  const climate = litterClimate(BIOME_WEATHER[s0.planets[planetIndex].archetype as keyof typeof BIOME_WEATHER] ?? {});
  let densitySeed = 0, densityFor: PlanetState | null = null;
  const dirOf = (p: Vec3) => { const c = planet().position, x = p.x - c.x, y = p.y - c.y, z = p.z - c.z, l = Math.hypot(x, y, z) || 1; return { x: x / l, y: y / l, z: z / l }; };
  const forestSeed = () => { const pl = planet(); if (pl !== seedFor) { seedFor = pl; seed = planetForestSeed(pl); } return seed; };
  const footprints = (): Disc[] => {
    const st = state();
    if (st.structures === discsFor) return discs;
    discsFor = st.structures;
    const pl = planet();
    discs = st.structures.filter(s => s.planetIndex === planetIndex && !s.drive && s.surfaceDir && s.health > 0).map(s => {
      const d = s.surfaceDir!, l = Math.hypot(d.x, d.y, d.z) || 1, r = structureFootprint(s, st.config, pl.radius) + person;
      return { x: d.x / l, y: d.y / l, z: d.z / l, cos: Math.cos(r / pl.radius) };
    });
    return discs;
  };
  return {
    person,
    up: dirOf,
    surface: (p: Vec3) => {
      const pl = planet(), d = dirOf(p), r = drawnGround ? drawnGround(pl, d) : personSurfaceRadius(pl, d, ps);
      return { x: pl.position.x + d.x * r, y: pl.position.y + d.y * r, z: pl.position.z + d.z * r };
    },
    biome: (p: Vec3) => forestBiomeAt(planet(), forestSeed(), person, dirOf(p), state().forest?.byClimate ?? false),
    material: (p: Vec3) => groundMaterialAt(planet(), dirOf(p)),
    blocked: (p: Vec3) => { const d = dirOf(p); return footprints().some(q => d.x * q.x + d.y * q.y + d.z * q.z > q.cos); },
    density: (p: Vec3) => {
      const pl = planet(), d = dirOf(p), k = pl.radius / (LITTER.regionPeople * person);
      if (pl !== densityFor) { densityFor = pl; densitySeed = [...forestSeed()].reduce((a, ch) => (Math.imul(a, 31) + ch.charCodeAt(0)) | 0, 7); }
      return litterDensity({ x: d.x * k, y: d.y * k, z: d.z * k }, densitySeed, forestBiomeAt(pl, forestSeed(), person, d, state().forest?.byClimate ?? false), groundMaterialAt(pl, d), climate);
    },
  };
}
